import cn from 'classnames';
import { ComponentProps, forwardRef, HTMLProps } from 'react';
import { Tooltip } from '../../features/tooltip/tooltip';
import { SizeType, VariantType } from '../../types/props';
import s from './icon-button.module.css';

export interface IconButtonProps extends Omit<HTMLProps<HTMLButtonElement>, 'size' | 'onClick'> {
  size?: SizeType;
  variant?: VariantType;
  tooltip?: string;
  tooltipProps?: Partial<ComponentProps<typeof Tooltip>>;
  outline?: boolean;
  onClick?: (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => void;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>((props, ref) => {
  const {
    size = 'md',
    variant = 'primary',
    className,
    disabled,
    tabIndex,
    tooltip,
    tooltipProps,
    children,
    outline,
    ...other
  } = props;

  const classNames = cn(
    s.component,
    className,
    s[size],
    s[variant],
    disabled && s.disabled,
    outline && s.outline,
  );

  const content = <span className={classNames}>{children}</span>;

  return tooltip ? (
    <Tooltip
      {...tooltipProps}
      wrapperClassName={s.tooltipWrapper}
      optionsWrapperClassName={s.tooltip}
      trigger={content}
      behavior="hover"
      disabled={disabled}
      position="top"
      tabIndex={tabIndex}
      onClickTrigger={other.onClick}
      customTrigger={document.body}
      offset={4}
    >
      {tooltip}
    </Tooltip>
  ) : (
    <button
      {...other}
      ref={ref}
      className={classNames}
      tabIndex={tabIndex ?? (disabled ? -1 : 0)}
      type="button"
      disabled={disabled}
    >
      {children}
    </button>
  );
});
